import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

// Auth token lives in an httpOnly cookie (backend-managed). We cache only the
// non-sensitive user profile in localStorage so the UI can render immediately.
const USER_CACHE_KEY = "mascot_user";
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const s = localStorage.getItem(USER_CACHE_KEY);
    return s ? JSON.parse(s) : null;
  });
  const [loading, setLoading] = useState(false);

  const persist = useCallback((u) => {
    if (u) {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
    } else {
      localStorage.removeItem(USER_CACHE_KEY);
    }
    setUser(u);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    persist(data.user);
    return data.user;
  }, [persist]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    persist(data.user);
    return data.user;
  }, [persist]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    persist(null);
  }, [persist]);

  // On mount, verify the cookie is still valid by calling /me
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/auth/me")
      .then((r) => { if (!cancelled) persist(r.data); })
      .catch(() => { if (!cancelled) persist(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [persist]);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
